import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import { Globe, Camera, Save, Loader2 } from 'lucide-react';
import { HeatmapLayer } from './HeatmapLayer'; 
import 'leaflet/dist/leaflet.css';
import { 
  Users, 
  Megaphone, 
  TrendingUp, 
  MessageSquare, 
  MapPin, 
  Heart,
  BarChart3,
  Share2,
  UserCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext'; 
import { CampaignLinkGenerator } from './CampaignLinkGenerator';

interface PoliticalDashboardProps {
  onViewChange: (view: string) => void;
}

export function PoliticalDashboard({ onViewChange }: PoliticalDashboardProps) {
  const { user } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Add this
  const [branding, setBranding] = useState({ name: '', photo: null as File | null });

  // Missing states found in your fetch function
  const [fullName, setFullName] = useState('');
  const [shopName, setShopName] = useState('');
  const [products, setProducts] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Get shopId from your auth user
  const shopId = user?.shop_id; 
  
  const stats = [
    { label: 'Voter Reach', value: '45.2k', icon: Users, color: 'text-blue-600', trend: '+12%' },
    { label: 'Endorsements', value: '12,840', icon: Heart, color: 'text-red-600', trend: '+5.4%' },
    { label: 'Active Ads', value: '24', icon: Megaphone, color: 'text-purple-600', trend: 'Steady' },
    { label: 'Engagement', value: '18.5%', icon: MessageSquare, color: 'text-green-600', trend: '+2.1%' },
  ];

  const [profile, setProfile] = useState({ name: '', photo_url: '', primary_color: '#2563eb' });

  const quickActions = [
    { id: 'manifestos', title: 'Manage Manifestos', icon: Megaphone, desc: 'Update policy points' },
    { id: 'branding', title: 'Candidate Branding', icon: UserCircle, desc: 'Profile photo & display name' },
    { id: 'analytics', title: 'Voter Insights', icon: BarChart3, desc: 'Demographic breakdown' },
    { id: 'visitor-engagement', title: 'Town Hall Chats', icon: MessageSquare, desc: 'Respond to voters' },
    { id: 'qr-generation', title: 'Rally QR Codes', icon: Share2, desc: 'Check-in at events' },
    { id: 'diaspora-hub', title: 'Diaspora Connect', icon: Globe, desc: 'Fundraising & US Town Halls' },
  ];

  const fetchfull_name = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/fetch-politician-metadata', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'fetch_metadata', shop_id: shopId })
      });
      const result = await response.json();

      const data = Array.isArray(result) ? result[0] : result;

      if (data && data.full_name) {
        setProfile({
          name: data.full_name,
          // Prepend your Supabase/Storage base URL if photo_url is just a partial path
          photo_url: data.photo_url.startsWith('http') 
                     ? data.photo_url 
                     : `https://{data.photo_url}`,
          primary_color: '#2563eb' // Default or dynamic if available
        });
      }


    } catch (error) { console.error("Fetch failed:", error); } finally { setIsLoading(false); }
  };

   useEffect(() => {
    if (shopId) fetchfull_name();
    return () => { if(imagePreview) URL.revokeObjectURL(imagePreview); }
  }, [shopId]);

  const handleBrandingSave = async () => {
    if (!branding.name && !branding.photo) return alert("Please provide a name or photo.");
    setIsUploading(true);
    
    const formData = new FormData();
    if (branding.photo) formData.append('photo', branding.photo);
    formData.append('name', branding.name);
    formData.append('shop_id', user?.shop_id?.toString() ?? "");

    try {
      await fetch('https://n8n.tenear.com/webhook/upload-metadata', {
        method: 'POST',
        body: formData
      });
      alert("Branding updated successfully!");
    } catch (error) {
      console.error("Upload failed", error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* 1. Header Section */}
      <div 
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-4 rounded-xl shadow-sm border-l-4 gap-4" 
        style={{ borderLeftColor: profile.primary_color }}
      >
        <div className="flex items-center gap-4">
          {profile.photo_url ? (
            <img src={profile.photo_url} alt="Profile" className="w-16 h-16 rounded-full object-cover border-2 shadow-sm" style={{ borderColor: profile.primary_color }} />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center text-gray-400">
              <UserCircle size={40} />
            </div>
          )}
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
              {profile.name || "Campaign Command Centre"}
            </h2>
            <p className="text-sm text-gray-500">Real-time mobilization for 2027.</p>
          </div>
        </div>
        <button 
          style={{ backgroundColor: profile.primary_color }}
          onClick={() => onViewChange('campaigns')}
          className="w-full sm:w-auto text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity shadow-md font-medium text-center"
        >
          Launch New Ad
        </button>
      </div>

      {/* 2. Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
            <div className="flex justify-between items-start">
              <div className={`p-2 rounded-lg bg-gray-50 ${stat.color}`}>
                <stat.icon className="w-6 h-6" />
              </div>
              <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                {stat.trend}
              </span>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-500">{stat.label}</p>
              <h3 className="text-2xl font-bold">{stat.value}</h3>
            </div>
          </div>
        ))}
      </div>

      {/* 3. Main Action Grid & Branding */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary-600" /> Quick Actions
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {quickActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => onViewChange(action.id)}
                  className="flex items-start gap-3 p-4 rounded-lg border border-gray-100 hover:border-primary-200 hover:bg-primary-50 transition-all text-left group"
                >
                  <action.icon className="w-5 h-5 text-gray-400 group-hover:text-primary-600" />
                  <div>
                    <p className="font-medium text-gray-900">{action.title}</p>
                    <p className="text-xs text-gray-500">{action.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* New Branding & Metadata Upload Section */}
          <div className="bg-white p-6 rounded-xl border border-blue-100 shadow-sm bg-gradient-to-br from-white to-blue-50">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-blue-900">
              <Camera className="w-5 h-5 text-blue-600" /> Candidate Branding
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Display Name</label>
                <input 
                  type="text" 
                  placeholder="Hon. Jane Doe"
                  className="w-full border border-gray-200 p-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  onChange={(e) => setBranding({...branding, name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Profile Photo (for WhatsApp Preview)</label>
                <div className="relative border-2 border-dashed border-gray-200 rounded-lg p-4 hover:bg-white transition-colors cursor-pointer">
                  <input 
                    type="file" 
                    className="absolute inset-0 opacity-0 cursor-pointer" 
                    onChange={(e) => setBranding({...branding, photo: e.target.files?.[0] || null})}
                  />
                  <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
                    {branding.photo ? <span className="text-blue-600 font-medium">{branding.photo.name}</span> : <><Upload className="w-4 h-4" /> Click to upload image</>}
                  </div>
                </div>
              </div>
              <button 
                onClick={handleBrandingSave}
                disabled={isUploading}
                className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Branding Metadata
              </button>
            </div>
          </div>
        </div>

        <CampaignLinkGenerator shopId={user?.shop_id?.toString() ?? ""} />
      </div>

      {/* 4. Geographical Reach */}
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-primary-600" /> Constituency Hotspots
        </h3>
        <div className="rounded-lg overflow-hidden border border-gray-200">
           <ConstituencyHotspots shopId={user?.shop_id?.toString() ?? ""} />
        </div>
      </div>
    </div>
  );
}

const ConstituencyHotspots = ({ shopId }: { shopId: string }) => {
  const [geoData, setGeoData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [heatPoints] = useState<[number, number, number][]>([
    [-1.286, 36.817, 0.8], 
    [-1.292, 36.821, 0.5]
  ]);

  useEffect(() => {
    setIsLoading(true);
    fetch('/data/kenya_constituencies.json')
      .then(res => res.json())
      .then(data => {
        if (data && data.type === "FeatureCollection") setGeoData(data);
      })
      .catch(err => console.error("Map Error:", err))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) return <div className="h-[500px] flex items-center justify-center bg-gray-50">Loading Map...</div>;

  return (
    <div style={{ height: '500px', width: '100%' }}>
      <MapContainer center={[0.0236, 37.9062]} zoom={6} style={{ height: '100%', width: '100%' }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {geoData && <GeoJSON data={geoData} style={{ fillColor: "transparent", weight: 1, color: "#666" }} />}
        <HeatmapLayer points={heatPoints} />
      </MapContainer>
    </div>
  );
};

const Upload = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
  </svg>
);
